import { useNavigate } from 'react-router-dom';
import { Home, ArrowRight } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';

const Projects = () => {
  const navigate = useNavigate();
  const { projects, loading, error } = useProjects();

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <nav className="sm:hidden fixed top-0 left-0 right-0 bg-black border-b border-white/10 z-50">
        <div className="px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              <span className="text-sm">Home</span>
            </button>
            
            <button
              onClick={() => navigate('/research')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              Research
            </button>
            
            <button
              onClick={() => navigate('/writings')}
              className="text-white/60 hover:text-white transition-colors text-sm"
            >
              Writings
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-8 pb-8 sm:pb-12">
        <nav className="hidden sm:block mb-12">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>
            
            <button
              onClick={() => navigate('/research')}
              className="text-white/60 hover:text-white transition-colors"
            >
              Research
            </button>
            
            <button
              onClick={() => navigate('/writings')}
              className="text-white/60 hover:text-white transition-colors"
            >
              Writings
            </button>
          </div>
        </nav>
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-light">Projects</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-white/40">Loading projects...</div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-red-400">Error loading projects: {error}</div>
        )}

        {/* Projects List */}
        {!loading && !error && (
          <div className="space-y-6 sm:space-y-8">
            {projects.length === 0 ? (
              <div className="text-white/40">No projects found. Add some from the admin panel!</div>
            ) : (
              projects.map((project) => (
                <a
                  key={project.id}
                  href={`https://${project.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col sm:flex-row gap-4 sm:gap-5 items-start border-b border-white/10 pb-6 sm:pb-8 last:border-b-0 cursor-pointer"
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    loading="lazy"
                    width="64"
                    height="64"
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover border border-white/10 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-xl sm:text-2xl font-light text-white group-hover:text-white/80 transition-colors">
                          {project.title}
                        </h3>
                        <span
                          className={`text-xs px-2 py-1 rounded ${project.status === 'Live'
                              ? 'bg-black-500/20 text-gray-400'
                              : 'bg-black-500/20 text-gray-400'
                            }`}
                        >
                          {project.status}
                        </span>
                      </div>
                      <ArrowRight className="hidden sm:block w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0" />
                    </div>
                    <div className="text-white/40 text-xs sm:text-sm mb-2 sm:mb-3 break-all">{project.url}</div>
                    <p className="text-white/60 text-sm sm:text-base leading-relaxed mb-3">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {project.tech.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="text-xs px-2 py-0.5 rounded bg-white/10 text-white/50"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </a>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;